import type { ContenuVitrine } from '@/features/marketing/i18n/contenu';

/**
 * **The English copy** (#2374).
 *
 * ⚠️ **« Portage salarial » is rendered as “employment umbrella”**, and the term is used
 * consistently: a *porté* is an **umbrella employee**, a *société de portage* an **umbrella
 * company**. The French term has no exact English equivalent, and the whole vocabulary of the
 * product hangs on the word chosen — settling it once is what keeps the site readable.
 *
 * ⚠️ **No amount appears here.** The pricing grid is read from the billing provider and served by
 * the API : translating it would mean copying a second grid, and the one that bills would not be the
 * one displayed (#1290).
 */
export const CONTENU_EN: ContenuVitrine = {
  trialLabel: 'Book a demo',
  trialLabelCourt: 'Demo',
  navLabels: {
    '/': 'Home',
    '/fonctionnalites': 'Features',
    '/avantages': 'Benefits',
    '/applications-mobiles': 'Mobile apps',
    '/espace-web': 'Web app',
    '/tarifs': 'Pricing',
    '/le-metier': 'The business',
    '/modules': 'Modules',
  },
  hero: {
    badge: 'The ERP for employment umbrella companies',
    titreDebut: 'The whole business,',
    titreAccent: 'from contract to invoice',
    accroche:
      'Suivora covers the business end to end — employee records, assignments, monthly timesheets, expense claims, HR approval and invoicing. With two mobile apps: one for your contractors, one for your staff.',
    mentionSansEngagement: 'No commitment — a guided demo, from contract to invoice.',
    preuves: ['Hosted in France', 'Isolated per organisation', 'Two apps, iOS & Android'],
  },
  metier: {
    eyebrow: 'The business, not one more tool',
    title: 'An umbrella company is not a spreadsheet of days',
    lead: 'It is an employee file to keep, activity to approve, an invoice to issue and pay to release — every month, for every umbrella employee.',
    points: [
      {
        title: 'The employee, from contract to departure',
        text: 'Permanent umbrella contract or partner status, identity documents, bank details, amendments: the file is built through a guided flow and signed electronically, with no PDFs going back and forth by email.',
      },
      {
        title: 'The month declared, then approved',
        text: 'Timesheets, absences, expense claims and meal vouchers all land in the same place. Your HR team gets a dedicated approval desk, not an inbox.',
      },
      {
        title: 'The client invoice and the pay',
        text: 'Invoicing starts from the assignment and its approved activity. Partner dividend distributions follow the same route, with the same audit trail.',
      },
    ],
  },
  modules: {
    eyebrow: 'Functional coverage',
    title: 'A complete ERP, not a module you will have to round out',
    lead: 'Everything below is in the product today, on the same data and under the same permissions.',
    domaines: [
      {
        title: 'Contractors & contracts',
        text: 'The employee file, complete and current.',
        items: [
          'Directory and contractor records',
          'Guided onboarding',
          'Permanent umbrella and partner statuses',
          'Documents and electronic signature',
          'Absences and leave',
        ],
      },
      {
        title: 'Activity & receipts',
        text: 'What the employee declares, what HR approves.',
        items: [
          'Monthly timesheets',
          'Expense claims and mileage rates',
          'Meal vouchers',
          'HR approval desk',
        ],
      },
      {
        title: 'Sales & assignments',
        text: 'Where the business comes from, and who it pays.',
        items: [
          'Clients and signatories',
          'Assignments and staffing',
          'Introducers and commissions',
          'Employee referrals',
        ],
      },
      {
        title: 'Finance & steering',
        text: 'What goes out as an invoice, what comes back as a figure.',
        items: [
          'Invoicing per assignment',
          'Partner dividends',
          'Reports and exports',
          'Dashboards and statistics',
        ],
      },
    ],
    extras: [
      'Roles and permissions',
      'Audit log',
      'Bulk imports',
      'Internal announcements',
      'Knowledge base',
      'Support',
      'Integrations',
      'Deletion requests',
    ],
  },
  apercu: {
    eyebrow: 'Go further',
    title: 'Three pages for the detail',
    lead: 'Mobile apps, the web app and pricing — the essentials here, the detail on their own page.',
    teasers: [
      {
        eyebrow: 'Mobile apps',
        title: 'Your contractors, without a computer',
        text: 'Timesheets, expense claims and documents from iOS and Android — and approvals for those waiting on them.',
      },
      {
        eyebrow: 'Web app',
        title: 'Two spaces, one product',
        text: 'The contractor and the firm’s back office: each sees only their own, on the same data.',
      },
      {
        eyebrow: 'Pricing',
        title: 'You pay per billable employee',
        text: 'The grid in force, exactly as it is billed — the more you carry, the less each one costs.',
      },
    ],
  },
  clients: {
    eyebrow: 'They trust us',
    title: 'Firms running their business on Suivora',
  },
  /* ⚠️ Drafts until the person quoted approves them — see `temoignages.ts` (#2523). */
  temoignages: {
    eyebrow: 'In their words',
    title: 'What Suivora changed in their day-to-day',
    lead: 'One feature, one person who uses it, and what changed for them.',
    pause: 'Pause',
    reprendre: 'Resume',
    choisir: 'Choose a testimonial',
    items: [
      {
        fonctionnalite: 'Client portal delivery',
        fonction: 'Executive assistant',
        citation:
          'Some of our clients want neither invoices nor timesheets by e-mail: they have to be uploaded to their own portal. Those instructions lived in our heads, and a miss only surfaced at the reminder. Now the portal and the references to quote are stored on the client or the mission, the invoice waits for me under “To upload”, and I record the upload date. When I send a reminder, the client reads when it was uploaded.',
      },
      {
        fonctionnalite: 'Purchase order tracking',
        fonction: 'Umbrella consultant',
        citation:
          'I never knew where I stood on my purchase order. Now I see it while I fill in my timesheet: what the client ordered, what I have already declared, what is left. When the end gets close, I raise it with the client before the invoice, not after.',
      },
      {
        fonctionnalite: 'Announcements to consultants',
        fonction: 'HR assistant',
        citation:
          'Our updates to consultants went out by e-mail and got lost in inboxes. With announcements, they land in their space, and I can see who has read them. I follow up with those who haven’t, instead of resending everything to everyone.',
      },
      {
        fonctionnalite: 'Built-in e-signature',
        fonction: 'Partner',
        citation:
          'Getting partners to sign a document meant one e-mail per person and a spreadsheet to track who had signed. Now I upload the PDF, pick the signers and follow everything in one place — without paying for yet another signature tool.',
      },
      {
        fonctionnalite: 'From approved timesheet to invoice',
        fonction: 'Billing manager',
        citation:
          'An approved timesheet becomes a draft invoice: the days come from the timesheet, the rate from the mission. I review and issue, I no longer retype anything — and I no longer find discrepancies in front of the client.',
      },
    ],
  },
  ora: {
    eyebrow: 'Built-in assistant',
    title: 'Ora answers the questions nobody has time to answer twice',
    lead: 'Every firm answers the same questions ten times a month: when am I reimbursed, why was this timesheet rejected, who approves before whom. Ora answers them from the product documentation, in both workspaces.',
    nom: 'Ora',
    nature: 'AI assistant',
    points: [
      {
        title: 'It cites what it read',
        text: 'Every answer names the documentation sections it rests on. You can check — that is what separates an assistant from an oracle.',
      },
      {
        title: 'It does not make things up',
        text: '“That is not in the documentation” is an answer it gives, and it is the right one. A plausible but wrong payroll rule goes unnoticed: it shows up, you believe it, you act on it.',
      },
      {
        title: 'For your contractors and your managers alike',
        text: 'Two screens, one foundation: the contractor asks how to file expenses, the manager how onboarding a contractor works.',
      },
    ],
    limite:
      'Ora answers about how the product works, not about a case file: it reads no timesheet, no amount, no leave balance, and says so rather than guessing. Questions are processed within the European Union.',
  },
  confiance: {
    eyebrow: 'Trust',
    title: 'The questions you will be asked before signing',
    lead: 'You handle employment contracts, payslips and bank details. Here is what the product guarantees, mechanism by mechanism.',
    points: [
      {
        title: 'Isolation per organisation',
        text: 'Each organisation is served on its own subdomain, with its own data, accounts and permissions — nothing is shared between two entities.',
      },
      {
        title: 'Explicit roles and permissions',
        text: 'Access is a right granted, not a status inherited. Roles are composed module by module.',
      },
      {
        title: 'Audit log',
        text: 'Sensitive actions leave a timestamped, readable trace that outlives the person who took them.',
      },
      {
        title: 'Your GDPR obligations',
        text: 'Account deletion requests are handled inside the tool, and data is hosted in France.',
      },
    ],
  },
  tarifs: {
    titre: {
      eyebrow: 'Pricing',
      title: 'You pay by the number of umbrella employees',
      lead: 'A model that follows your activity rather than your licence count — and costs less per employee as you grow.',
    },
    points: [
      {
        title: 'One counter: the employees you have invoiced',
        text: 'An employee counts if at least one invoice was issued for them over the last six months. Not per module, not per back-office user, not per signed document.',
      },
      {
        title: 'Tiered, and decreasing',
        text: 'The rate of the tier reached applies to the whole volume. The more employees you carry, the less each one costs.',
      },
      {
        title: 'Everything included',
        text: 'Every module, both spaces and the mobile apps. No option billed separately, no minimum term.',
      },
    ],
    grilleEnVigueur: 'Grid in force',
    grilleLead:
      'The tiers exactly as they are billed — read from our billing provider, never copied.',
    palier: 'Tier',
    tarif: 'Rate',
    parPorteEtParMois: 'per month, per employee',
    auForfaitParMois: 'flat, per month',
    etUnForfaitDe: 'plus a flat fee of',
    exemple: 'An example',
  },
  avantages: {
    titre: {
      eyebrow: 'Benefits',
      title: 'What Suivora does differently',
      lead: 'Eight deliberate choices, and what they change once the product is live.',
    },
    chiffres: {
      eyebrow: 'In figures',
      title: 'What is in the product today',
      lead: 'Verifiable numbers, not rounded ones: they describe what is delivered.',
    },
    differences: [
      {
        eyebrow: 'In the field',
        title: 'A timesheet is approved from a phone',
        problem:
          'Approving usually takes a desk, a spreadsheet export and a reminder by email — and the approval waits until someone is back at the office.',
        answer:
          'The approver gets a notification and answers in two taps. An outside client approves through a link, with no account to create.',
      },
      {
        eyebrow: 'Permissions',
        title: 'Ninety-six permissions, not three profiles',
        problem:
          'An “HR profile” that opens everything an HR manager might do also opens what they must not see: salaries, dividends, the security policy.',
        answer:
          'Every screen has its own key, and settings are split tab by tab. A role that sets payment terms does not open the password policy. You name your own roles, and the editor shows what each box actually grants.',
      },
      {
        eyebrow: 'Figures',
        title: 'A missing figure is never shown as zero',
        problem:
          'A dashboard that replaces missing data with zero shows “€0 invoiced” for a firm that does invoice — and nobody notices, because a zero does not stand out.',
        answer:
          'What the server does not serve is not displayed. A partial total says it is partial, and screens check their own numbers — a headcount reconciles with its arrivals and departures.',
      },
      {
        eyebrow: 'Tools',
        title: 'Your tools stay yours',
        problem:
          'An ERP that imposes its own payroll, invoicing and signature forces you to change four tools to adopt one.',
        answer:
          'Each family connects separately — payroll, invoicing, signature, banking. What the chosen connector can do is stated, and so is what it cannot.',
      },
      {
        eyebrow: 'Key accounts',
        title: 'The client who wants their portal gets it',
        problem:
          'Key accounts no longer accept invoices by email: they must be uploaded to their supplier portal. The tool sends them anyway, the upload happens on the side, and nobody knows whether it took place — until the invoice goes unpaid.',
        answer:
          'The portal is set on the client, or for a single assignment. Nothing is sent by email: the invoice and the timesheet wait “to upload”, with the access at hand and encrypted. The upload is recorded, and the reminder says “uploaded to your portal on …”.',
      },
      {
        eyebrow: 'Migration',
        title: 'We take over the history, not just the names',
        problem:
          'A migration limited to records means re-entering years of timesheets, expenses and invoices — or keeping the old tool open for the history.',
        answer:
          'Seven chained imports cover clients, contractors, assignments, contacts, timesheets, expense claims and invoices. Each runs as a dry run first, and states what it will skip.',
      },
      {
        eyebrow: 'Brand',
        title: 'Your contractors see your name',
        problem:
          'A tool that shows its own brand to the employee is a monthly reminder that the firm rents software.',
        answer:
          'Subdomain, logo, colour, legal notice and document issuer are the firm’s own — in the app as on letters and invoices.',
      },
      {
        eyebrow: 'Statuses',
        title: 'Umbrella employment and partner status, in one product',
        problem:
          'A firm running both statuses usually keeps two tools, or one tool and a spreadsheet.',
        answer:
          'Activity accounts on one side, profit shares on the other, each with their screens and their permissions. A firm only sees the status it practises.',
      },
      {
        eyebrow: 'Traceability',
        title: 'Anything touching money or permissions is logged',
        problem:
          'Without a log, a simple question — who changed that rate, and when — has no answer, and gets settled from memory.',
        answer:
          'A readable, exportable audit log, fed by the writes themselves rather than by an intention to log.',
      },
    ],
    figures: [
      { label: 'mobile apps', detail: 'contractor and manager, both native' },
      { label: 'permissions in your editor', detail: 'across 17 domains' },
      { label: 'migration imports', detail: 'each with a dry run' },
      { label: 'connector families', detail: 'payroll, invoicing, signature, banking' },
    ],
  },
  espaceWeb: {
    titre: {
      eyebrow: 'Web app',
      title: 'And on a large screen, the whole surface',
      lead: 'Mobile carries the daily gesture; the web carries what needs room — the whole month, the lists, the totals, the exports and the decisions that commit. Both spaces have their own.',
    },
    moments: [
      {
        alt: 'A contractor’s timesheet in the browser',
        title: 'The whole month in view',
        text: 'The full calendar, days worked, absences and on-call in one place — plus “Fill the month” for when nothing changes. The phone handles the daily touch-up; the computer handles the month.',
      },
      {
        alt: 'A contractor’s financial overview in the browser',
        title: 'What they invoice, what they take home',
        text: 'Revenue, net pay, dividends and expense claims on the same scale, month after month, with the conversion rate. This is the reading a phone screen can only summarise.',
      },
    ],
    points: [
      'Lists and tables, paginated and filterable',
      'Invoicing and accounting exports',
      'Statistics and reports',
      'Roles, permissions and the audit log',
    ],
    espaces: [
      {
        tag: 'Contractor',
        title: 'They declare, they justify, they sign',
        text: 'On mobile day to day, on the web when a large screen is needed. Same account, same data.',
        items: [
          'Timesheets, absences and expense claims',
          'Documents and electronic signature',
          'Revenue and assignments',
          'Referrals and internal announcements',
        ],
      },
      {
        tag: 'Back office & HR',
        title: 'They steer, approve and invoice',
        text: 'The umbrella company’s full back office, with permissions granted module by module.',
        items: [
          'Contractors, clients and assignments',
          'Timesheet and expense approval',
          'Invoicing and dividends',
          'Statistics, reports and audit',
        ],
      },
    ],
  },
  espaces: {
    titre: {
      eyebrow: 'Two spaces, one truth',
      title: 'Each their own interface, the same data',
      lead: 'What the contractor declares on the 30th is what HR approves on the 2nd, and what the invoice picks up on the 5th. No export, no re-entry, no parallel version.',
    },
    cycle: [
      {
        title: 'The employee declares',
        text: 'The month’s timesheet, absences and expense claims, entered from the mobile app as the days go by.',
      },
      {
        title: 'HR approves',
        text: 'A dedicated approval desk picks up day discrepancies and missing receipts, with no re-entry.',
      },
      {
        title: 'The invoice goes out',
        text: 'Invoicing draws on the assignment and its approved activity. Introducer commissions and dividends follow.',
      },
    ],
  },
  mobile: {
    titre: {
      eyebrow: 'Mobile apps',
      title: 'Two native apps, not a website folded onto a screen',
      lead: 'One for your contractors, one for your staff. This is where day-to-day operations actually happen: a timesheet filled in on the train arrives on time, and an approval you can give while travelling does not hold up invoicing.',
    },
    consultantMoments: [
      {
        alt: 'Timesheet and expense claims in the contractor app',
        title: 'Their month, declared with a thumb',
        text: 'One tap sets a half day, two a full day, a long press switches to remote — the app says so at the top of the calendar. Expenses follow, receipt photographed on the spot, and anything missing is flagged before sending.',
      },
      {
        alt: 'The contractor home screen and their month-by-month finances',
        title: 'Their figures, without having to ask',
        text: 'Revenue for the month and for the assignment, timesheet progress, expenses incurred: it is all on the home screen. The detail goes down to the month — invoiced revenue, pay, expense claims — and the statement can be downloaded.',
      },
    ],
    consultantFeatures: [
      {
        title: 'The timesheet fills in with a thumb',
        text: 'One tap sets a half day, two a full day, a long press switches to remote. A month is declared in under a minute, without a keyboard.',
      },
      {
        title: 'The receipt reads itself',
        text: 'Photograph the receipt: the amount, the date and the merchant are picked up, and all that is left is to check them. What you already entered is never overwritten.',
      },
      {
        title: 'The receipt never leaves our servers',
        text: 'Reading happens where the image was uploaded: it is neither downloaded, nor copied, nor sent anywhere else. It leaves neither our servers nor Europe.',
      },
      {
        title: 'One expense claim, one photo',
        text: 'The receipt is captured on the spot. Mileage applies the rate in force, and the app flags a missing receipt before sending.',
      },
      {
        title: 'Documents are signed on the phone',
        text: 'Contracts, amendments and certificates go out for electronic signature and are signed from the app — no printer, no scanner.',
      },
      {
        title: 'Their position, without having to ask',
        text: 'Revenue for the month, cumulative revenue on the assignment, seniority, expenses reimbursed: the employee has their figures on the home screen.',
      },
    ],
    pushExamples: [
      'Timesheet to send',
      'Missing receipt',
      'Document to sign',
      'Expenses reimbursed',
    ],
    adminMoments: [
      {
        alt: 'The admin app home screen and the approval of a timesheet',
        title: 'See what is waiting, and deal with it',
        text: 'The home screen says how many files need a decision, and which to start with. A timesheet filed the day before invoicing is approved on the spot, without reopening the back office.',
      },
      {
        alt: 'The assignment list and creating an assignment in three steps',
        title: 'Set up an assignment between two meetings',
        text: 'Client, period, staffing: the essentials fit in one step, the next two refine the contract and can wait. The assignment exists before you are back at the office.',
      },
      {
        alt: 'Creating a contractor and the contractor directory',
        title: 'Bring a contractor on board',
        text: 'You only enter the essentials: the employee completes their own profile through the onboarding link. The directory then shows who is on assignment, who is still to be approved, and what is still missing.',
      },
    ],
    adminFeatures: [
      {
        title: 'Approve a late timesheet',
        text: 'A contractor sends their timesheet on the evening of the 3rd, the day before invoicing. Approval happens in the app, without reopening the back office.',
      },
      {
        title: 'Onboard a contractor',
        text: 'Create the account, start the arrival flow and track the documents still missing — a new employee does not wait for you to get back from a trip.',
      },
      {
        title: 'Create an assignment',
        text: 'Client, period, staffing: an assignment is set up from the phone, between two meetings, rather than late in the evening.',
      },
      {
        title: 'Let nothing slip',
        text: 'Anything awaiting a decision comes through as a notification: timesheet to approve, expense claim, document to sign, absence request.',
      },
    ],
  },
  fonctionnalites: {
    titre: {
      eyebrow: 'Features',
      title: 'What you open on Monday morning',
      lead: 'The detail of each domain, as it exists in the product today. Nothing in the future tense.',
    },
    transversal: {
      eyebrow: 'At no extra cost',
      title: 'What runs through every screen',
      lead: 'These seven belong to no module: they apply everywhere, for everyone.',
      points: [
        {
          title: 'Two mobile apps',
          text: 'One for the contractor — timesheets, expenses, absences, payslips. One for the manager — approve, staff, follow up. These are not websites in phone format.',
        },
        {
          title: 'Connectors of your choosing',
          text: 'Payroll, invoicing, electronic signature, banking: each family is chosen independently, and what the chosen connector can do is stated on screen.',
        },
        {
          title: 'Migration of what you have',
          text: 'Seven chained imports — clients, contractors, assignments, contacts, timesheets, expense claims, invoices — each with a dry run before anything is written.',
        },
        {
          title: 'In the firm’s colours',
          text: 'Subdomain, logo, accent colour, legal notice and document issuer: the contractor sees their employer’s brand, not ours.',
        },
        {
          title: 'Electronic signature included',
          text: 'Contracts and amendments are signed inside the product, with timestamp and audit trail. An external provider can take over.',
        },
        {
          title: 'Internal communication',
          text: 'Announcements, blog, social posts and notifications: what the firm has to say to its employees starts here.',
        },
        {
          title: 'Ora, the built-in assistant',
          text: 'An AI assistant that answers about how the product works, citing the documentation sections it read. In both workspaces, at no extra cost.',
        },
      ],
    },
    galerie: {
      eyebrow: 'In pictures',
      title: 'Four more screens',
      lead: 'The ones no single domain carries on its own, and that best show how the product works.',
      images: [
        {
          alt: 'Invoicing screen: its counters, including credit notes counted separately, and the invoice list',
          caption:
            'Invoicing in one screen: what is invoiced, what is left to issue, what is awaiting payment — and a credit note counted separately, never folded into the total.',
        },
        {
          alt: 'The organisation’s role list, each card showing its name, description and permission count',
          caption:
            'The roles are yours: you name them, you choose what each one opens, and the number of permissions granted is on the card.',
        },
        {
          alt: 'Migration sequence: the seven imports in order, each stating what it depends on',
          caption:
            'Migration, in the order it actually happens. Each step states what it depends on, and nothing is written until you have run the simulation.',
        },
        {
          alt: 'Integrations screen: the processing chain and the four connector cards',
          caption:
            'Payroll, invoicing, signature, banking: four links, chosen separately. Each connector states what it can do — and what it cannot.',
        },
      ],
    },
    domaines: [
      {
        title: 'Contractors and contracts',
        lead: 'The employee file, from arrival to departure.',
        items: [
          {
            label: 'A complete contractor record',
            detail:
              'Civil status, contact details, emergency contact, medical check, residence permit, bank details — every field is corrected from the screen, without going through an import.',
          },
          {
            label: 'Guided onboarding',
            detail:
              'The employee fills in their own file from their space; HR checks it, then activates the account. Files awaiting review show on the dashboard.',
          },
          {
            label: 'Dated arrivals and departures',
            detail:
              'Planned departure and actual departure are two distinct dates: a notice period under way is not a departure. Employment status follows the actual departure, never the other way round.',
          },
          {
            label: 'Two pay statuses',
            detail:
              'Standard umbrella employment and partner status paid in dividends coexist, each with their own screens — a firm only sees what it practises.',
          },
          {
            label: 'Absences and leave',
            detail:
              'Balance, calendar, approval. A manager can declare on someone’s behalf, and that is a permission distinct from approving.',
          },
        ],
      },
      {
        title: 'Activity and receipts',
        lead: 'What the employee declares, what HR approves.',
        items: [
          {
            label: 'Timesheets',
            detail:
              'One timesheet per assignment and per month, with activities, on-call and remote work. The entry grid fits on a phone screen.',
          },
          {
            label: 'External approval',
            detail:
              'The client approves the timesheet through a link, with no account to create. Their answer comes back into the file and goes to accounting.',
          },
          {
            label: 'Expense claims',
            detail:
              'Photographed receipt, category, VAT, rebilling to the client. Mileage and long-distance travel are computed, not typed in.',
          },
          {
            label: 'Meal vouchers',
            detail: 'A monthly order derived from days worked and absences, sent to the provider.',
          },
          {
            label: 'Approval desk',
            detail:
              'A single queue for timesheets and expense claims, with the rejection reason written once and read by the person concerned.',
          },
        ],
      },
      {
        title: 'Sales and assignments',
        lead: 'Where the business comes from, and who it commits.',
        items: [
          {
            label: 'Clients, contacts and signatories',
            detail:
              'Three distinct roles on the same company: who signs, who approves timesheets, who receives invoices. Confusing them costs a lost invoice.',
          },
          {
            label: 'Invoicing through an intermediary',
            detail:
              'The end client and the invoiced company can differ, and the chain is readable from both records.',
          },
          {
            label: 'Assignments, purchase orders and renewals',
            detail:
              'Days sold, days given, period and order reference. A renewal follows on without re-entering the assignment.',
          },
          {
            label: 'Introducers and referrals',
            detail:
              'An introducer follows their own files from their own space, and their commissions are computed on the firm’s scale.',
          },
        ],
      },
      {
        title: 'Invoicing and finance',
        lead: 'What goes out as an invoice, what comes in at the bank.',
        items: [
          {
            label: 'Invoices and credit notes',
            detail:
              'Issued from the assignment and the approved timesheet, with the VAT wording the status requires — exemption, intra-EU reverse charge, out of scope.',
          },
          {
            label: 'Chasing unpaid invoices',
            detail:
              'By client or in one pass, with a history of what has already gone out. Chasing is a permission distinct from issuing.',
          },
          {
            label: 'Upload to the client’s portal',
            detail:
              'For the client who refuses email — Coupa, Ariba, Chorus Pro: nothing is sent, the invoice and the timesheet wait “to upload”, the portal access is kept encrypted, and the upload is recorded with its date. The reminder quotes it.',
          },
          {
            label: 'Bank reconciliation',
            detail:
              'Payment is dated by the reconciled transaction, not by the day the sync saw it. An invoice settled without a transaction says so rather than inventing a date.',
          },
          {
            label: 'Dividends and activity accounts',
            detail:
              'Depending on the status: partners’ profit shares, or the employee’s activity account with its financial reserve.',
          },
          {
            label: 'Early-payment discounts and payment terms',
            detail:
              'Catalogues kept by the firm, applied to the assignment then to the invoice — never entered twice.',
          },
        ],
      },
      {
        title: 'Steering',
        lead: 'Figures that check out.',
        items: [
          {
            label: 'Dashboard',
            detail:
              'The queues awaiting an action, and nothing else at the top of the screen: timesheets, expense claims, documents to sign, unpaid invoices.',
          },
          {
            label: 'Overviews by domain',
            detail:
              'Assignments, Directory, Finance, Communication: each group has its own, with monthly series over three years.',
          },
          {
            label: 'Headcount',
            detail:
              'Arrivals, departures and headcount, kept apart because a stock and a flow do not read on the same axis. The series states what it leaves out.',
          },
          {
            label: 'Annual report',
            detail:
              'Invoiced, collected, payroll, management fees and average collection time, per financial year.',
          },
          {
            label: 'Email analytics and deliverability',
            detail:
              'What people open, what bounces, and why. A message that does not arrive is visible before the client reports it.',
          },
        ],
      },
      {
        title: 'Administration',
        lead: 'Who is allowed what, and who did what.',
        items: [
          {
            label: 'Roles and permissions',
            detail:
              'A hundred permissions in the catalogue, grouped by domain. Settings are split tab by tab: setting a payment term does not open the password policy.',
          },
          {
            label: 'Audit log',
            detail:
              'Who changed what, when, and from where. Exportable, and sensitive writes are recorded without exception.',
          },
          {
            label: 'Portal accounts',
            detail:
              'Invitation, reset, account deletion in the GDPR sense, with an export of the person’s data.',
          },
          {
            label: 'Internal profiles',
            detail:
              'The firm’s own staff — employees, apprentices — with their documents and absences, kept distinct from contractors.',
          },
        ],
      },
    ],
  },
  support: {
    badge: 'Support',
    titre: 'A question, an incident: here is where to go',
    accroche:
      'Not every request goes to the same place. Anything touching your contract or your pay goes back to the company that employs you; anything touching the product comes to its publisher.',
    routesEyebrow: 'Three routes',
    routesTitre: 'Where your request belongs',
    routes: [
      {
        tag: 'The most direct',
        title: 'From the app',
        text: 'The mobile apps and the web app both carry a “Contact us” screen. It is the shortest route: your request arrives already attached to your account and your company.',
      },
      {
        tag: 'For anything touching your contract',
        title: 'Your umbrella company',
        text: 'Contract, payslip, timesheet, expense claim, access to your account: they decide and they answer. Suivora is the tool they use, not their employer.',
      },
      {
        tag: 'This form',
        title: 'The product’s publisher',
        text: 'A platform malfunction, a question about the product, or a request about your data that your company cannot handle: the form opposite reaches us.',
      },
    ],
    formulaireEyebrow: 'Write to the publisher',
    formulaireTitre: 'Describe your request',
    formulaireTexte:
      'We reply to the address you give, and you receive a reference to quote if you follow up.',
    sujets: ['A malfunction', 'A question about the product', 'My personal data'],
  },
  formulaire: {
    confirmation: 'Your request has arrived',
    nom: 'Your name',
    nomPlaceholder: 'First and last name',
    societe: 'Your company',
    societePlaceholder: 'Registered name',
    email: 'Email address',
    emailPlaceholder: 'first.last@company.com',
    telephone: 'Phone',
    facultatif: 'Optional',
    portes: 'Umbrella employees',
    portesPlaceholder: 'e.g. 45',
    portesHint: 'A number, even a rough one.',
    objet: 'Subject',
    objetPlaceholder: 'In one line',
    motifs: ['Discover the product', 'Replace our current tool', 'Quote and pricing'],
    autre: 'Other',
    motifManquant: 'Choose a reason, or “Other”.',
    message: 'Your message',
    messagePlaceholder:
      'Your current tools, what you are looking to replace, your constraints — what you would like to see during the demo.',
    envoyer: 'Send my request',
    piege: 'Do not fill in',
    erreurIndisponible: 'The form is not live yet. Please try again in a few days.',
    erreurTropDeDemandes:
      'Too many requests sent from this address. Please try again in a few minutes.',
    erreurEnvoi: 'Sending failed. Your message is still here — try again in a moment.',
    emailRaison: 'It is our only way to reply to you.',
  },
  demo: {
    badge: 'Demo',
    titre: 'See Suivora running, from contract to invoice',
    accroche:
      'We show you the product in action, on a complete demonstration environment — and we answer your own cases as we go.',
    preuves: ['Guided demo', 'Mobile apps included'],
    etapesEyebrow: 'After you send',
    etapesTitre: 'Three steps, and nothing to install',
    etapes: [
      {
        title: 'You describe your business',
        text: 'How many umbrella employees you carry, which contract statuses you run, your current tools. Three minutes is enough.',
      },
      {
        title: 'We prepare the walkthrough',
        text: 'We choose what to show you: the contract statuses and the modules that match what you actually do.',
      },
      {
        title: 'We walk you through it',
        text: 'The product in action, screen by screen: an employee, an assignment, a timesheet, expenses, through to the client invoice. Your questions as we go.',
      },
    ],
    verrezTitre: 'What you will see',
    verrez: [
      'The full path, from contract to client invoice',
      'The modules that concern your business',
      'Both mobile apps, iOS and Android',
      'The pricing tier that applies to you, with figures',
    ],
    formulaireEyebrow: 'Demo request',
    formulaireTitre: 'Tell us about your business',
    formulaireTexte: 'We reply to the address you give, with the pricing tier that applies to you.',
  },
  ctaFinal: {
    titre: 'See Suivora on your own organisation',
    texte: 'A guided demo, from contract to invoice, on an environment prepared for your business.',
    arguments: ['Guided demo', 'Mobile apps included'],
  },
  pied: {
    support: 'Support',
    mentionsLegales: 'Legal notice',
    confidentialite: 'Privacy',
    conditions: 'Terms',
  },
  autreLangue: 'Français',
  autreLangueCourte: 'FR',
};
